// src/context/brandVoice/ui/VoiceForm.jsx — ONE language of the "Personalità del brand" (v190).
//
// One row per axis: the axis label on the left, a growing box on the right (wireframe 07).
// ⛔ The rows are EXACTLY the `axes` handed in — the server's, in its order (decision 8: no add,
// no remove; the vocabulary is closed server-side and an unknown key is dropped in silence).
// ⛔ No real line breaks (the writer reads each axis as one bullet line): Enter is blocked and a
// pasted newline becomes a space.
// ⛔ It validates nothing: the checks run on save (`voiceModel.voiceProblems`); it only shows,
// under each input, OUR sentence for the problems keyed to it (`voice.<lang>.<key>`).
// Every change hands up a WHOLE new value, plus the key it changed (so the page can clear that
// key's problem).
// ⭐ No "Nome" field (revised 2026-10-06): a workspace has ONE personality, so its name is never shown — the save
// fills it in (advancedModel.voiceLabelForSave) — and the form is just the axes, every one, in the server's order.
import React from 'react';
import { useTranslation } from 'react-i18next';
import GrowingTextarea, { blockEnter, oneLine } from './GrowingTextarea.jsx';
import { INPUT } from '../widgets/listStyles.js';
import { voicePath } from '../voiceModel.js';
import { COLORS, FONT_SANS, SEMANTIC } from '../../../constants/theme.js';

const ROW = {
  display: 'grid', gridTemplateColumns: 'minmax(110px, 190px) minmax(0, 1fr)',
  gap: '6px 16px', alignItems: 'start', padding: '8px 0',
};
const LABEL = { fontSize: 13, fontWeight: 600, color: COLORS.ink, paddingTop: 13 };
const PROBLEM = { marginTop: 6, fontSize: 12, lineHeight: '18px', color: SEMANTIC.errore.fg };
const EMPTY = { label: '', axes: {} };

export default function VoiceForm({
  lang = 'it', value, axes = [], problems = {}, disabled = false, onChange,
}) {
  const { t } = useTranslation('context');
  const current = value || EMPTY;
  const problemsAt = (key) => (problems && problems[voicePath(lang, key)]) || [];
  const sentences = (key) => problemsAt(key).map((p, i) => (
    <div key={`${p.key}-${i}`} role="alert" style={PROBLEM}>{t(`brandVoice.validation.${p.key}`)}</div>
  ));

  const axisRow = ({ axis, label }) => {
    const invalid = problemsAt(axis).length > 0;
    const guidance = current.axes ? current.axes[axis] : undefined;
    return (
      <div key={axis} style={ROW}>
        {/* The visible label; the box carries the same text as its accessible name. */}
        <span aria-hidden="true" style={LABEL}>{label}</span>
        <div>
          <GrowingTextarea
            aria-label={label}
            aria-invalid={invalid || undefined}
            value={typeof guidance === 'string' ? guidance : ''}
            disabled={disabled}
            onKeyDown={blockEnter}
            onChange={(e) => onChange?.(
              { ...current, axes: { ...(current.axes || {}), [axis]: oneLine(e.target.value) } },
              axis,
            )}
            style={{ ...INPUT(invalid), width: '100%' }}
          />
          {sentences(axis)}
        </div>
      </div>
    );
  };

  return (
    <div style={{ fontFamily: FONT_SANS }}>
      {axes.map(axisRow)}
    </div>
  );
}
